<script>
import {
    mdiBellOutline,
    mdiCheckAll,
    mdiRefresh,
    mdiCheckCircle,
    mdiCloseCircle,
    mdiInformationOutline,
    mdiPencilOutline,
    mdiPlusCircleOutline,
    mdiDeleteOutline,
} from "@mdi/js";
import apiService from "@/apiService";
import processErrors from "@/js/ProcessErrors";
import formatDate from "@/js/formatDate.js";
import AppToast from "@/components/Common/AppToast.vue";

export default {
    components: { AppToast },
    data() {
        return {
            menu: false,
            loading: false,
            markingAll: false,
            notifications: [],
            unread_count: 0,
            mdiBellOutline,
            mdiCheckAll,
            mdiRefresh,
            mdiCheckCircle,
            mdiCloseCircle,
            mdiInformationOutline,
            pollTimer: null,
        };
    },
    created() {
        this.pendingToasts = [];
        this.pollAbort = new AbortController();
        if (!this.isLoggedIn()) return;
        this.fetchUnread();
        // this.pollTimer = setInterval(() => this.fetchUnread(), 5000);
        this.pollTimer = setInterval(() => this.fetchUnread(), 60000);
    },
    mounted() {
        this.flushToasts();
    },
    beforeUnmount() {
        if (this.pollTimer) clearInterval(this.pollTimer);
        if (this.pollAbort) this.pollAbort.abort();
    },
    watch: {
        menu(open) {
            if (open) this.fetchNotifications();
        },
    },
    methods: {
        formatDate,
        isLoggedIn() {
            return !!localStorage.getItem("token");
        },
        async fetchUnread() {
            if (!this.isLoggedIn()) return;
            try {
                const response = await apiService(
                    "notifications",
                    "get",
                    { page: 1 },
                    false,
                    this.pollAbort?.signal,
                );
                if (response.status !== false) {
                    const items = this.notificationItems(response);
                    this.unread_count =
                        response.data?.unread_count ??
                        response.unread_count ??
                        this.unread_count ??
                        0;
                    if (this.menu) {
                        this.notifications = items;
                    }
                    this.syncUnreadToasts(items);
                }
            } catch (error) {
                // silent for background poll
            }
        },
        notificationItems(response) {
            const bucket =
                response?.data?.notifications ?? response?.notifications;
            if (Array.isArray(bucket)) return bucket;
            if (Array.isArray(bucket?.data)) return bucket.data;
            return [];
        },
        isUnread(notification) {
            return (
                notification && !notification.read_at && notification.id != null
            );
        },
        showToast(text, onClick) {
            const toast = this.$refs.toast;
            if (!toast || typeof toast.show !== "function") {
                this.pendingToasts.push({ text, onClick });
                return;
            }
            toast.show(text, { onClick });
        },
        flushToasts() {
            if (!this.pendingToasts?.length) return;
            const queued = this.pendingToasts.splice(0);
            queued.forEach((item) => this.showToast(item.text, item.onClick));
        },
        openNotifications() {
            this.menu = false;
            if (this.$route.path !== "/notifications") {
                this.$router.push("/notifications");
            }
        },
        pushToasts(list, total = list.length) {
            const cap = 3;
            const visible = list.slice(0, cap);
            [...visible].reverse().forEach((notification) => {
                this.showToast(this.notificationTitle(notification), () => {
                    this.handleClick(notification);
                });
            });
            const extra = total - visible.length;
            if (extra > 0) {
                this.showToast(
                    `${extra} more unread notification${
                        extra === 1 ? "" : "s"
                    }`,
                    () => this.openNotifications(),
                );
            }
        },
        syncUnreadToasts(items) {
            const unread = items.filter((notification) =>
                this.isUnread(notification),
            );
            const total = Math.max(this.unread_count || 0, unread.length);
            if (total === 0) return;
            if (unread.length === 0) {
                this.showToast(
                    total === 1
                        ? "1 unread notification"
                        : `${total} unread notifications`,
                    () => this.openNotifications(),
                );
                return;
            }
            this.pushToasts(unread, total);
        },
        async fetchNotifications() {
            try {
                this.loading = true;
                const response = await apiService("notifications", "get");
                if (response.status === false) {
                    processErrors(response);
                } else {
                    this.notifications = this.notificationItems(response);
                    this.unread_count =
                        response.data?.unread_count ??
                        response.unread_count ??
                        0;
                }
            } catch (error) {
                processErrors(error);
            } finally {
                this.loading = false;
            }
        },
        async markAllRead() {
            if (this.unread_count === 0) return;
            try {
                this.markingAll = true;
                const response = await apiService(
                    "notifications/read-all",
                    "put",
                    {},
                );
                if (response.status === false) {
                    processErrors(response);
                    return;
                }
                this.notifications = this.notifications.map((n) => ({
                    ...n,
                    read_at: n.read_at || new Date().toISOString(),
                }));
                this.unread_count = 0;
            } catch (error) {
                processErrors(error);
            } finally {
                this.markingAll = false;
            }
        },
        async markRead(notification) {
            if (notification.read_at) return;
            try {
                const response = await apiService(
                    `notifications/${notification.id}/read`,
                    "put",
                    {},
                );
                if (response.status !== false) {
                    notification.read_at = new Date().toISOString();
                    this.unread_count = Math.max(0, this.unread_count - 1);
                }
            } catch (error) {
                // ignore
            }
        },
        getNotifIcon(n) {
            const data = n.data || {};
            if (
                data.type === "component_edit_requested" ||
                data.type === "bridge_component_edit_requested"
            ) {
                return { icon: mdiPencilOutline, color: "warning" };
            }
            if (data.type === "component_create_requested") {
                return { icon: mdiPlusCircleOutline, color: "info" };
            }
            if (data.type === "material_usage_delete_requested") {
                return { icon: mdiDeleteOutline, color: "warning" };
            }
            if (data.status === "approved")
                return { icon: mdiCheckCircle, color: "success" };
            if (data.status === "rejected")
                return { icon: mdiCloseCircle, color: "error" };
            return { icon: mdiInformationOutline, color: "grey" };
        },
        statusVerb(status) {
            if (status === "approved") return "approved";
            if (status === "rejected") return "rejected";
            return "updated";
        },
        notificationTitle(n) {
            const data = n.data || {};
            switch (data.type) {
                case "component_edit_decided":
                    return `Component edit ${this.statusVerb(data.status)}`;
                case "bridge_component_edit_decided":
                    return `Bridge component edit ${this.statusVerb(
                        data.status,
                    )}`;
                case "component_edit_requested":
                    return "New component edit request";
                case "bridge_component_edit_requested":
                    return "New bridge component edit request";
                case "component_create_requested":
                    return "New component create request";
                case "component_create_decided":
                    return `Component create ${this.statusVerb(data.status)}`;
                case "material_usage_delete_requested":
                    return "New material usage delete request";
                case "material_usage_delete_decided":
                    return `Material usage delete ${this.statusVerb(
                        data.status,
                    )}`;
                default:
                    return "Notification";
            }
        },
        componentLabel(data) {
            const parts = [];
            if (data.component_code) parts.push(data.component_code);
            if (data.serial_no) parts.push(`S/N ${data.serial_no}`);
            return parts.join(" ");
        },
        materialUsageLabel(data) {
            const parts = [];
            if (data.ticket_id) parts.push(`Ticket ${data.ticket_id}`);
            if (data.load_id != null && data.load_id !== "") {
                parts.push(`Load ${data.load_id}`);
            }
            return parts.join(" / ");
        },
        appendActor(line, name) {
            if (!name) return line;
            return line ? `${line} by ${name}` : `By ${name}`;
        },
        appendNote(line, note) {
            if (!note) return line;
            return line ? `${line} — ${note}` : note;
        },
        materialUsageDeleteRoute(data) {
            const query = {};
            if (data.type === "material_usage_delete_requested") {
                query.status = "pending";
            } else if (
                data.status === "approved" ||
                data.status === "rejected"
            ) {
                query.status = data.status;
            }
            if (data.project_id) {
                query.project_id = String(data.project_id);
            }
            return { path: "/WH/material-usages/manage", query };
        },
        changesSummary(changes) {
            if (!changes || typeof changes !== "object") return "";
            return Object.values(changes)
                .map((c) => {
                    const label = c?.label || "";
                    return `${label}: ${c?.from ?? "-"} → ${c?.to ?? "-"}`;
                })
                .join(", ");
        },
        notificationDescription(n) {
            const data = n.data || {};
            switch (data.type) {
                case "bridge_component_edit_decided":
                case "component_edit_decided": {
                    let line = this.componentLabel(data);
                    if (data.decided_by_name) {
                        line += line
                            ? ` by ${data.decided_by_name}`
                            : `By ${data.decided_by_name}`;
                    }
                    if (data.status === "rejected" && data.rejection_reason) {
                        line += ` — ${data.rejection_reason}`;
                    }
                    return line;
                }
                case "bridge_component_edit_requested":
                case "component_edit_requested": {
                    let line = this.componentLabel(data);
                    if (data.requested_by_name) {
                        line += line
                            ? ` by ${data.requested_by_name}`
                            : `By ${data.requested_by_name}`;
                    }
                    const summary = this.changesSummary(data.changes);
                    if (summary) {
                        line += line ? ` — ${summary}` : summary;
                    }
                    return line;
                }
                case "component_create_requested": {
                    let line = this.componentLabel(data);
                    if (data.requested_by_name) {
                        line += line
                            ? ` by ${data.requested_by_name}`
                            : `By ${data.requested_by_name}`;
                    }
                    return line;
                }
                case "component_create_decided": {
                    let line = this.componentLabel(data);
                    line = this.appendActor(line, data.decided_by_name);
                    if (data.status === "rejected") {
                        line = this.appendNote(line, data.rejection_reason);
                    }
                    return line;
                }
                case "material_usage_delete_requested": {
                    let line = this.materialUsageLabel(data);
                    line = this.appendActor(line, data.requested_by_name);
                    return this.appendNote(line, data.reason);
                }
                case "material_usage_delete_decided": {
                    let line = this.materialUsageLabel(data);
                    line = this.appendActor(line, data.decided_by_name);
                    if (data.status === "rejected") {
                        line = this.appendNote(line, data.rejection_reason);
                    }
                    return line;
                }
                default:
                    return "";
            }
        },
        async handleClick(n) {
            await this.markRead(n);
            const data = n.data || {};
            if (
                data.type === "component_edit_decided" ||
                data.type === "component_edit_requested" ||
                data.type === "component_create_requested" ||
                data.type === "component_create_decided" ||
                data.type === "bridge_component_edit_requested" ||
                data.type === "bridge_component_edit_decided"
            ) {
                this.menu = false;
                if (this.$route.path !== "/component-modified-request") {
                    this.$router.push("/component-modified-request");
                }
                return;
            }
            if (
                data.type === "material_usage_delete_requested" ||
                data.type === "material_usage_delete_decided"
            ) {
                this.menu = false;
                this.$router.push(this.materialUsageDeleteRoute(data));
            }
        },
    },
};
</script>

<template>
    <div class="notif-menu-root">
        <AppToast ref="toast" :timeout="10000" />
        <v-menu
            v-model="menu"
            :close-on-content-click="false"
            location="bottom end"
            offset="8"
        >
            <template #activator="{ props }">
                <button
                    v-bind="props"
                    type="button"
                    class="notif-btn cursor-pointer"
                    aria-label="Notifications"
                >
                    <v-badge
                        :content="unread_count > 99 ? '99+' : unread_count"
                        :model-value="unread_count > 0"
                        color="error"
                        offset-x="2"
                        offset-y="2"
                    >
                        <v-icon :icon="mdiBellOutline" size="18"></v-icon>
                    </v-badge>
                </button>
            </template>

            <v-card width="380" class="np-card" elevation="0">
                <!-- Header -->
                <div class="np-header">
                    <div class="d-flex align-center ga-2">
                        <span class="np-title">Notifications</span>
                        <span v-if="unread_count > 0" class="np-badge">{{
                            unread_count
                        }}</span>
                    </div>
                    <div class="d-flex align-center ga-1">
                        <v-btn
                            :prepend-icon="mdiCheckAll"
                            size="x-small"
                            variant="text"
                            class="np-action-btn"
                            :disabled="unread_count === 0 || markingAll"
                            :loading="markingAll"
                            @click="markAllRead"
                            >Mark all read</v-btn
                        >
                        <v-btn
                            :icon="mdiRefresh"
                            size="x-small"
                            variant="text"
                            class="np-action-btn"
                            :loading="loading"
                            @click="fetchNotifications"
                        ></v-btn>
                    </div>
                </div>

                <!-- List -->
                <div class="np-list">
                    <div
                        v-if="loading && notifications.length === 0"
                        class="np-empty"
                    >
                        Loading...
                    </div>
                    <div
                        v-else-if="notifications.length === 0"
                        class="np-empty"
                    >
                        <v-icon
                            :icon="mdiBellOutline"
                            size="32"
                            class="mb-2 np-empty-icon"
                        ></v-icon>
                        No notifications yet
                    </div>
                    <div
                        v-else
                        v-for="n in notifications"
                        :key="n.id"
                        class="np-item"
                        :class="{ 'np-item--unread': !n.read_at }"
                        @click="handleClick(n)"
                    >
                        <div class="np-icon-box">
                            <v-icon
                                :icon="getNotifIcon(n).icon"
                                :color="getNotifIcon(n).color"
                                size="18"
                            ></v-icon>
                        </div>
                        <div class="flex-grow-1" style="min-width: 0">
                            <div class="np-item-title">
                                {{ notificationTitle(n) }}
                            </div>
                            <div
                                v-if="notificationDescription(n)"
                                class="np-item-desc"
                            >
                                {{ notificationDescription(n) }}
                            </div>
                            <div class="np-item-time">
                                {{ formatDate(n.created_at) }}
                            </div>
                        </div>
                        <span v-if="!n.read_at" class="np-dot"></span>
                    </div>
                </div>

                <!-- Footer -->
                <div
                    class="np-footer"
                    @click="
                        menu = false;
                        $router.push('/notifications');
                    "
                >
                    View all notifications
                </div>
            </v-card>
        </v-menu>
    </div>
</template>

<style lang="scss" scoped>
.notif-menu-root {
    display: contents;
}

// ── Light mode defaults ───────────────────────────────────────────────────────
.np-card {
    --np-sep: rgba(0, 0, 0, 0.06);
    --np-hover: rgba(0, 0, 0, 0.03);
    --np-muted: #6b7280;
    --np-dim: #9ca3af;
    --np-unread-bg: rgba(0, 229, 160, 0.07);
    --np-unread-hover: rgba(0, 229, 160, 0.13);
    --np-accent: #00a87a;
    --np-accent-soft: #00c98f;
    --np-badge-bg: #d0fdf0;
    --np-badge-text: #00805d;
    --np-dot: #00c98f;

    background: var(--card) !important;
    border-radius: 16px !important;
    overflow: hidden;
    border: 1px solid var(--border) !important;
}

// ── Dark mode overrides ───────────────────────────────────────────────────────
:global(.v-theme--dark) .np-card {
    --np-sep: rgba(255, 255, 255, 0.05);
    --np-hover: rgba(255, 255, 255, 0.04);
    --np-muted: rgba(255, 255, 255, 0.45);
    --np-dim: rgba(255, 255, 255, 0.25);
    --np-unread-bg: rgba(0, 229, 160, 0.1);
    --np-unread-hover: rgba(0, 229, 160, 0.18);
    --np-accent: #00e5a0;
    --np-accent-soft: #6fffd4;
    --np-badge-bg: #00e5a0;
    --np-badge-text: #002d1f;
    --np-dot: #00e5a0;
}

// ── Styles ────────────────────────────────────────────────────────────────────
.np-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 16px 12px;
    border-bottom: 1px solid var(--border);
}

.np-title {
    font-size: 15px;
    font-weight: 700;
    color: var(--card-foreground);
    letter-spacing: -0.2px;
}

.np-badge {
    background: var(--np-badge-bg);
    color: var(--np-badge-text);
    font-size: 11px;
    font-weight: 600;
    padding: 1px 8px;
    border-radius: 20px;
    line-height: 1.7;
}

.np-action-btn {
    color: var(--np-muted) !important;
    font-size: 12px !important;
    transition: color 0.15s;

    &:hover {
        color: var(--np-accent) !important;
    }
}

.np-list {
    max-height: 440px;
    overflow-y: auto;
}

.np-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px 16px;
    color: var(--np-dim);
    font-size: 13px;
    gap: 4px;
}

.np-item {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 13px 16px;
    cursor: pointer;
    transition: background 0.15s;
    border-bottom: 1px solid var(--np-sep);

    &:last-child {
        border-bottom: none;
    }

    &:hover {
        background: var(--np-hover);

        .np-icon-box {
            background: var(--card);
        }
    }

    &--unread {
        background: rgba(var(--np-unread-bg), 0.5);

        &:hover {
            background: var(--np-unread-hover);
        }
    }
}

.np-icon-box {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    background: var(--muted);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.np-item-title {
    font-size: 13px;
    font-weight: 600;
    color: var(--card-foreground);
    margin-bottom: 3px;
    line-height: 1.3;
}

.np-item-desc {
    font-size: 12px;
    color: var(--muted-foreground);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    margin-bottom: 4px;
}

.np-item-time {
    font-size: 11px;
    color: var(--muted-foreground);
}

.np-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--np-dot);
    flex-shrink: 0;
    margin-top: 4px;
}

.np-footer {
    padding: 13px 16px;
    text-align: center;
    font-size: 13px;
    font-weight: 500;
    color: var(--card-foreground);
    cursor: pointer;
    border-top: 1px solid var(--border);
    transition: color 0.15s;

    &:hover {
        color: var(--primary);
    }
}

.notif-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    padding: 0;
    border: 1px solid transparent;
    border-radius: var(--radius-sm);
    background: transparent;
    color: var(--muted-foreground);
    transition: color 0.15s ease, background-color 0.15s ease,
        border-color 0.15s ease;

    &:hover {
        color: var(--foreground);
        background: var(--muted);
        border-color: var(--border);
    }

    &:focus-visible {
        outline: 2px solid var(--ring);
        outline-offset: 2px;
    }
}
</style>
